import { createSelector } from 'reselect';
import type {
    InstagramAutomationLifecycleStatus,
    SocialPostWithAutomation,
} from '../../types/instagramAutomation.types';
import { RootState } from '../root-reducer';

const selectSocialPostsState = (state: RootState) => state.socialPosts;

/** Read buzzbridge config directly — avoids circular import with selectors/selectors.ts */
const selectBuzzBridgeConfig = (state: RootState) => state.configuration?.globalConfiguration?.buzzbridge;

export const selectConnectedPlatforms = (state: RootState) => state.socialPosts?.connectedPlatforms ?? [];

export const selectAutomationStatus = (state: RootState) => state.socialPosts?.automationStatus ?? null;

export const selectSocialPostsLoadingContext = (state: RootState) => state.socialPosts?.loadingContext ?? false;

export const selectSocialPostsPublishing = (state: RootState) => state.socialPosts?.publishing ?? false;

export const selectSocialPostsError = (state: RootState) => state.socialPosts?.error ?? null;

/** Platform-wide admin toggle from global configuration. */
export const selectInstagramCommentAutomationGloballyEnabled = createSelector(
    selectBuzzBridgeConfig,
    buzzbridge => buzzbridge?.instagram_comment_automation_enabled === true,
);

/**
 * Comment automation is allowed only when both the admin global toggle and the backend status flag are on.
 */
export const selectInstagramAutomationFeatureEnabled = createSelector(
    [selectInstagramCommentAutomationGloballyEnabled, selectAutomationStatus],
    (globalEnabled, status) => globalEnabled && Boolean(status?.feature_enabled),
);

export const selectConnectedPlatformUsernames = createSelector(
    [selectConnectedPlatforms],
    (platforms): Record<'instagram' | 'tiktok' | 'youtube', string | undefined> => {
        const map: Record<'instagram' | 'tiktok' | 'youtube', string | undefined> = {
            instagram: undefined,
            tiktok: undefined,
            youtube: undefined,
        };
        (platforms ?? []).forEach(p => {
            const key = p.platform as 'instagram' | 'tiktok' | 'youtube';
            if (key === 'instagram' || key === 'tiktok' || key === 'youtube') {
                map[key] = p.platform_username;
            }
        });
        return map;
    },
);

export const selectInstagramAutomationReady = createSelector(
    [selectInstagramCommentAutomationGloballyEnabled, selectAutomationStatus],
    (globalEnabled, status) => globalEnabled && Boolean(status?.automation_scopes_granted),
);

export const selectInstagramPostsList = (state: RootState) => state.socialPosts?.instagramPostsList ?? [];

export const selectInstagramPostsListLoading = (state: RootState) =>
    state.socialPosts?.loadingInstagramPostsList ?? false;

export const selectInstagramPostsListPagination = createSelector([selectSocialPostsState], socialPosts => ({
    total: socialPosts?.instagramPostsListTotal ?? 0,
    page: socialPosts?.instagramPostsListPage ?? 1,
    totalPages: socialPosts?.instagramPostsListTotalPages ?? 0,
}));

export const selectTogglingAutomationPostIds = (state: RootState) => state.socialPosts?.togglingAutomationPostIds ?? {};

export const selectConfiguredInstagramPosts = createSelector([selectInstagramPostsList], posts =>
    (posts ?? []).filter(p => p.instagram_automation != null),
);

export type AutomationListFilter = 'all' | 'active' | 'disabled' | 'pending';

export function filterConfiguredPosts(
    posts: SocialPostWithAutomation[] | undefined,
    filter: AutomationListFilter,
): SocialPostWithAutomation[] {
    const configured = (posts ?? []).filter(p => p.instagram_automation != null);
    if (filter === 'all') return configured;
    if (filter === 'active') {
        return configured.filter(p => p.instagram_automation?.enabled && p.instagram_automation?.status === 'active');
    }
    if (filter === 'disabled') {
        return configured.filter(
            p => !p.instagram_automation?.enabled || p.instagram_automation?.status === 'disabled',
        );
    }
    if (filter === 'pending') {
        return configured.filter(p => p.instagram_automation?.status === 'pending_publish');
    }
    return configured;
}

export function automationStatusLabel(status: InstagramAutomationLifecycleStatus | undefined): string {
    if (!status) return 'unknown';
    return status.replace(/_/g, ' ');
}
